import type { ReactNode } from 'react';
import { Modal as MantineModal, ScrollArea } from '@mantine/core';
import { useI18n } from '@/shared/i18n';

const sizeMap = {
  default: 'md',
  large: 'lg',
  xl: 'xl',
  /** Fixed 90% of the viewport — do not retune per screen. */
  workspace: '90vw',
} as const;

export function Modal({
  isOpen,
  title,
  children,
  onClose,
  size = 'default',
  dismissible = true,
  testId,
  scrollable = false,
}: {
  isOpen: boolean;
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  size?: 'default' | 'large' | 'xl' | 'workspace';
  dismissible?: boolean;
  testId?: string;
  /** Scroll long content inside a max-height body (workbench detail). */
  scrollable?: boolean;
}) {
  const { t } = useI18n();
  const isWorkspace = size === 'workspace';

  return (
    <MantineModal
      opened={isOpen}
      onClose={onClose}
      title={title}
      size={sizeMap[size]}
      centered
      radius="lg"
      closeOnClickOutside={dismissible}
      closeOnEscape={dismissible}
      withCloseButton={dismissible}
      data-testid={testId}
      closeButtonProps={{
        'aria-label': t('common.close'),
        ...({ 'data-testid': 'modal-close-btn' } as Record<string, string>),
      }}
      styles={scrollable || isWorkspace ? {
        content: {
          width: isWorkspace ? '90vw' : undefined,
          maxWidth: isWorkspace ? '90vw' : undefined,
          height: isWorkspace ? '90vh' : undefined,
          maxHeight: isWorkspace ? '90vh' : 'min(92vh, 920px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: isWorkspace ? 'hidden' : undefined,
        },
        header: {
          alignItems: 'flex-start',
          flexShrink: 0,
        },
        title: {
          flex: 1,
          marginRight: '0.5rem',
        },
        body: {
          paddingTop: 0,
          flex: 1,
          minHeight: 0,
          height: isWorkspace ? '100%' : undefined,
          display: 'flex',
          flexDirection: 'column',
          overflow: isWorkspace ? 'hidden' : undefined,
        },
      } : undefined}
    >
      {scrollable ? (
        <ScrollArea.Autosize
          mah={isWorkspace ? 'calc(90vh - 4.5rem)' : 'min(78vh, 780px)'}
          type="auto"
          offsetScrollbars
          style={{ flex: 1 }}
        >
          {children}
        </ScrollArea.Autosize>
      ) : children}
    </MantineModal>
  );
}
